namespace Solovis.Reports.ReportHelpers.ProForma {

  /**
   * Per-interval data keys emitted by the IntervalStats builder + ProFormaEditorReportBuilder.
   * Each base gets `_<intervalCode>` appended on the line item's `data` dict
   * (e.g. `startValue_Q1`, `proFormaTx_Q1`). Use the named members (e.g.
   * `ProFormaIntervalField.StartValue`) to refer to a base without repeating the string literal.
   */
  export const ProFormaIntervalField = {
    startValue: "startValue",
    endValue: "endValue",
    netAmountIn: "netAmountIn",
    intervalFromDayId: "intervalFromDayId",
    intervalToDayId: "intervalToDayId",
    proFormaTx: "proFormaTx",
    proFormaTxPercentage: "proFormaTxPercentage",
    benchmarkAllocation: "benchmarkAllocation",
    allocation: "allocation",
  } as const;

  export type ProFormaIntervalFieldBase = keyof typeof ProFormaIntervalField;

  /** Separator between a field base and its interval code in a data key / column id. */
  const INTERVAL_KEY_SEPARATOR = "_";

  /** Prefix for per-cell warning keys stored on row data (e.g. `cellWarning_proFormaTx_Q1`). */
  export const CELL_WARNING_KEY_PREFIX = "cellWarning_";


  /**
   * Per-row data shape on a ProForma line item. Per-interval values live alongside the
   * fixed row-level keys, indexed by `<base>_<code>`. We model that with an index signature
   * because the interval codes are dynamic (Q1/Q2/Q3, M01..M12, etc.).
   */
  export type ProFormaLineItemData = {
    investmentId?: number;
    assetLabel?: string;
    compositeGroupNodeId?: number;
    compositeParentGroupNodeId?: number;
    isCompositeGroup?: boolean;
    isBenchmark?: boolean;
    /**
     * Server-emitted (ProFormaEditorReportBuilder): whether this investment's owner has a default
     * cash account. Save is disabled when any savable leaf has this `false`, since a pro forma
     * buy/sell needs a cash account to offset against.
     */
    ownerHasCashAccount?: boolean;
    /** Marks a group-level scaffold row (see AddGroupScaffoldTradeRows). */
    isProFormaScaffold?: boolean;
    /** Id of the Grouping Property (e.g. "Asset Class") a scaffold row belongs to. */
    propertyAssociationId?: number;
    /** Id of the tag value (e.g. "Equity") a scaffold row belongs to. */
    tagAssociationId?: number;
    /**
     * Server-emitted (ProFormaEditorReportBuilder): the investmentId of this leaf's resolved Default
     * Tx Cash Account, omitted when none resolves or it resolves to the investment itself. Lets the
     * client live-recompute that account's Investment Activity Settlement row on edit without a
     * network round trip per keystroke (#44970) -- see recomputeSettlementForEdit.
     */
    cashAccountInvestmentId?: number;
    [intervalField: string]: any;
  }

  export type ProFormaLineItem = {
    data: ProFormaLineItemData;
    index?: number;
    _nestedGroupKey?: string;
    [key: string]: any;
  }

  export type CellChange = {
    col: { id?: string; [key: string]: any };
    oldValue: any;
    newValue: any;
  }

  const isProFormaIntervalFieldBase = (prefix: string): prefix is ProFormaIntervalFieldBase => {
    return !!ProFormaIntervalField[prefix as ProFormaIntervalFieldBase];
  }

  /**
   * column id pattern: `<FieldBase>_<code>`.
   * `parseColumnId` returns null when the id doesn't match a ProForma column.
   */
  export type ParsedProFormaColumnId = {
    base: ProFormaIntervalFieldBase;
    intervalCode: string;
  }

  export function parseColumnId(columnId: string | undefined): ParsedProFormaColumnId | null {
    if (!columnId) {
      return null;
    }
    const [base, intervalCode] = columnId.split(INTERVAL_KEY_SEPARATOR);
    if (isProFormaIntervalFieldBase(base) && intervalCode) {
      return { base, intervalCode };
    }
    return null;
  }

  function intervalKey(base: ProFormaIntervalFieldBase, code: string): string {
    return `${base}${INTERVAL_KEY_SEPARATOR}${code}`;
  }

  export function getIntervalField(data: ProFormaLineItemData, base: ProFormaIntervalFieldBase, code: string): number | null {
    const value = data[intervalKey(base, code)];
    if (value === null || typeof value === "undefined" || value === "") {
      return null;
    }
    const numericValue = typeof value === "number" ? value : Number(value);
    return isNaN(numericValue) ? null : numericValue;
  }

  export function setIntervalField(data: ProFormaLineItemData, base: ProFormaIntervalFieldBase, code: string, value: number | null): void {
    data[intervalKey(base, code)] = value;
  }

  type ReportTable = Solovis.Reports.Parts.Tables.DataTableControl;

  const getTableColumnById = (table: ReportTable, column: string) => {
    return table.columnSet._columnIdToColumnMap[column];
  };

  // The table's `dataView.rows` is the source line-item list bound from
  // `dataSets.report.reportSet.lineItems` — i.e. the same server-built rows (leaves AND
  // composite group rows) that the edited `row` is a member of. We need the full list to
  // roll a leaf edit up into its ancestor group totals.
  const getAllLineItems = (table: ReportTable): ProFormaLineItem[] => {
    return table.dataView?.rows ?? [];
  };

  /**
   * Amount (`proFormaTx_<code>`) is the source of truth; the percentage only drives the amount when
   * the user is editing from the % field.
   *
   * - Editing the amount: percentage is cleared (the amount stands on its own).
   * - Editing the percentage: amount is recomputed (newAmount = pct * startValue), then percentage is
   *   re-derived from the rounded amount so the row reflects the actual stored value.
   * - Rolling forward into a following period (the cascade sets its start value): a period with a
   *   percentage re-derives its amount from the new start value; a period with a fixed amount keeps
   *   that amount. Either way the end value is start + amount, so empty/cleared periods roll the
   *   start value straight through (end = start) instead of stalling the cascade.
   */
  export function recomputeRowForChange(
    table: ReportTable,
    row: ProFormaLineItem,
    change: CellChange,
  ): void {
    const parsed = parseColumnId(change.col?.id);
    if (!parsed) {
      return;
    }

    const data = row.data;
    if (!data) {
      return;
    }
    // Grab interval number from after the "-" 
    const [intervalName, intervalNumber] = parsed.intervalCode.split("-")
    let currentIntervalNum = parseInt(intervalNumber);
    if (isNaN(currentIntervalNum)) {
      return;
    }
    let lastEndValue = recomputePeriodForRowChange(table, row, change);

    while (true) {
      currentIntervalNum++;
      const nextIntervalCode = `${intervalName}-${currentIntervalNum}`;
      const nextIntervalKey = intervalKey(ProFormaIntervalField.startValue, nextIntervalCode);
      // Escape loop if the nextInterval isn't a valid column
      if (!getTableColumnById(table, nextIntervalKey)) {
        break;
      }
      setIntervalField(data, ProFormaIntervalField.startValue, nextIntervalCode, lastEndValue)

      lastEndValue = recomputePeriodForRowChange(table, row, {...change, col: {id: nextIntervalKey}})
    }
  };

  /**
   * The startValue a scaffold row's Tx% is denominated against: its parent GROUP's own startValue,
   * not the scaffold's own (which is always 0 — see AddGroupScaffoldTradeRowsAsync, "a new trade
   * has no position of its own"). Null if the parent group can't be resolved.
   */
  function getParentGroupStartValue(table: ReportTable, data: ProFormaLineItemData, code: string): number | null {
    if (typeof data.compositeParentGroupNodeId !== "number") {
      return null;
    }
    const rows = getAllLineItems(table);
    const { groupsByNodeId } = getGroupIndex(table, rows);
    const parentGroup = groupsByNodeId.get(data.compositeParentGroupNodeId);
    return parentGroup ? getIntervalField(parentGroup.data, ProFormaIntervalField.startValue, code) : null;
  }

  // Returns new end value if it was updated and null if it was not
  export function recomputePeriodForRowChange(
    table: ReportTable,
    row: ProFormaLineItem,
    change: CellChange,
  ): number | null {
    const parsed = parseColumnId(change.col?.id);
    if (!parsed) {
      return null;
    }

    const data = row.data;
    if (!data) {
      return null;
    }

    const startValue = getIntervalField(data, ProFormaIntervalField.startValue, parsed.intervalCode);
    const warningKey = `${CELL_WARNING_KEY_PREFIX}${ProFormaIntervalField.proFormaTx}_${parsed.intervalCode}`;

    // A scaffold row's own startValue is always 0 (see AddGroupScaffoldTradeRowsAsync — "a new
    // trade has no position of its own"), so its Tx% is denominated against the parent GROUP's
    // startValue instead; a regular leaf uses its own. Using the scaffold's own (always-0)
    // startValue would make every percentage edit divide by zero, blanking both columns.
    const percentageDenominator = data.isProFormaScaffold === true
      ? getParentGroupStartValue(table, data, parsed.intervalCode)
      : startValue;

    if (parsed.base === ProFormaIntervalField.proFormaTx) {
      // Editing the amount clears the percentage and any warning.
      setIntervalField(data, ProFormaIntervalField.proFormaTxPercentage, parsed.intervalCode, null);
      delete data[warningKey];
    }
    else {
      // The percentage drives the amount — either the user edited the % cell, or the cascade moved
      // this period's start value and a stored % must re-scale against it. Only recompute when a
      // percentage is actually present; otherwise the amount is a fixed value and must be preserved
      // (don't clobber a downstream fixed amount just because the start value rolled forward).
      const percentage = getIntervalField(data, ProFormaIntervalField.proFormaTxPercentage, parsed.intervalCode);
      if (percentage !== null) {
        const hasDenominator = percentageDenominator !== null && percentageDenominator !== 0;
        const amount = hasDenominator ? percentage * percentageDenominator : null;
        setIntervalField(data, ProFormaIntervalField.proFormaTx, parsed.intervalCode, amount);
        // Re-derive percent from the rounded amount so the displayed percent equals amount / startValue (option B).
        const derivedPercentage = (hasDenominator && amount !== null) ? amount / percentageDenominator : null;
        setIntervalField(data, ProFormaIntervalField.proFormaTxPercentage, parsed.intervalCode, derivedPercentage);

        const isProFormaPhaseEnabled = Boolean((Solovis as any).Info?.userEligibleForProFormaPhaseII);
        if (isProFormaPhaseEnabled) {
          const noMarketValue = (startValue === null || startValue === 0) && percentage !== null && percentage !== 0;
          if (noMarketValue) {
            data[warningKey] = "$ amount cannot be calculated — no current market value available for this node.";
          } else {
            delete data[warningKey];
          }
        }
      }
    }

    const amount = getIntervalField(data, ProFormaIntervalField.proFormaTx, parsed.intervalCode) ?? 0;
    // Keep the period's actual in-period activity and layer the pro forma on top:
    // endValue = startValue + netAmountIn + proFormaTx. netAmountIn is the period's real net flow
    // (untouched by pro forma edits), so this mirrors the server's real-investment leaf projection
    // (actual End Value + pro forma) and keeps a live edit numerically identical to a reload. For a
    // genuine forward period with no actual flow netAmountIn is 0, reducing to startValue + amount.
    const netAmountIn = getIntervalField(data, ProFormaIntervalField.netAmountIn, parsed.intervalCode) ?? 0;
    const newEndValue = (startValue ?? 0) + netAmountIn + amount;
    setIntervalField(data, "endValue", parsed.intervalCode, newEndValue);
    return newEndValue;
  }

  /**
   * Per-interval bases that a leaf edit changes and that must therefore be re-summed onto the
   * ancestor group rows. `netAmountIn` is unaffected by pro forma edits, and `benchmarkAllocation`
   * lives only on group rows (the server removes it from leaves after summing — see
   * CalculateGroupLineItemValues), so neither is re-aggregated here. `proFormaTxPercentage` is
   * deliberately absent (SE-575): a summed percentage across accounts with unrelated bases is
   * meaningless.
   */
  const GROUP_ROLLUP_BASES: ProFormaIntervalFieldBase[] = [
    ProFormaIntervalField.proFormaTx,
    ProFormaIntervalField.startValue,
    ProFormaIntervalField.endValue,
  ];

  /**
   * Pull the interval codes off a row's data by inspecting its `netAmountIn_<code>` keys. We key off
   * netAmountIn (not startValue) because the builder emits it on every row/period — real-investment
   * leaves via IntervalStats, and owner/holding-only leaves + group rows explicitly as 0 — whereas
   * startValue can be absent on some rows, which would drop their edits from the save.
   */
  function getIntervalCodes(data: ProFormaLineItemData): string[] {
    const codes: string[] = [];
    for (const key in data) {
      const [base, intervalCode] = key.split(INTERVAL_KEY_SEPARATOR);
      if (base === ProFormaIntervalField.netAmountIn) {
        codes.push(intervalCode);
      }
    }
    return codes;
  }

  type GroupChildrenMap = Map<number, number[]>;

  /**
   * Build a map of composite group node id -> its direct child group node ids.
   * Mirrors the groupChildren map in ProFormaEditorReportBuilder.CalculateGroupLineItemValues:
   * only rows that carry a `compositeGroupNodeId` (i.e. group rows) participate; leaves are linked
   * to their group purely by `compositeParentGroupNodeId`.
   */
  function buildGroupChildren(rows: ProFormaLineItem[]): GroupChildrenMap {
    const groupChildren: GroupChildrenMap = new Map();
    const ensure = (id: number) => {
      if (!groupChildren.has(id)) {
        groupChildren.set(id, []);
      }
    };
    for (const row of rows) {
      const data = row.data;
      if (!data || typeof data.compositeGroupNodeId !== "number") {
        continue;
      }
      ensure(data.compositeGroupNodeId);
      if (typeof data.compositeParentGroupNodeId === "number") {
        ensure(data.compositeParentGroupNodeId);
        groupChildren.get(data.compositeParentGroupNodeId)?.push(data.compositeGroupNodeId);
      }
    }
    return groupChildren;
  }

  /**
   * All descendant group node ids beneath `groupId` (not including `groupId` itself).
   * Mirrors ProFormaEditorReportBuilder.GetDescendantGroupIds.
   */
  function getDescendantGroupIds(groupId: number, groupChildren: GroupChildrenMap): Set<number> {
    const result = new Set<number>();
    const children = groupChildren.get(groupId);
    if (!children) {
      return result;
    }
    for (const child of children) {
      result.add(child);
      getDescendantGroupIds(child, groupChildren).forEach(id => result.add(id));
    }
    return result;
  }

  /**
   * The immutable grouping topology of a loaded report: which group row each node id maps to, and
   * the full set of descendant leaves under each group. Both are derived purely from the
   * compositeGroupNodeId / compositeParentGroupNodeId structure, which never changes while editing
   * (only cell values do) — so this is built once per loaded report and reused across every edit.
   */
  type ProFormaGroupIndex = {
    groupsByNodeId: Map<number, ProFormaLineItem>;
    descendantLeavesByGroup: Map<number, ProFormaLineItem[]>;
    /** The root ("All") group — the composite group row with no parent group. */
    rootGroup: ProFormaLineItem | null;
    /**
     * Ids of leaf-turned-settlement-parents: isCompositeGroup === false rows that nonetheless carry a
     * compositeGroupNodeId (a cash-account leaf parenting its Investment Activity Settlement row —
     * see ProFormaEditorReportBuilder.AddInvestmentActivitySettlementRows). Mirrors the backend's
     * leafGroupNodeIds in CalculateGroupLineItemValues.
     */
    leafGroupNodeIds: Set<number>;
    /** Leaf row by `investmentId` — e.g. looking up a cash account's own leaf by its investment id. */
    leafByInvestmentId: Map<number, ProFormaLineItem>;
    /** A leaf-group's settlement child, by the leaf-group's `compositeGroupNodeId`. */
    settlementLeafByGroupNodeId: Map<number, ProFormaLineItem>;
    /** Every trade leaf resolving to a given cash-account `investmentId`, keyed by that id. */
    contributingLeavesByCashAccountInvestmentId: Map<number, ProFormaLineItem[]>;
  };

  /**
   * Build the grouping topology for the current rows. The descendant-leaf lists are the client-side
   * equivalent of the leaf gathering in ProFormaEditorReportBuilder.CalculateGroupLineItemValues:
   * a "group" here is anything descendant rows can roll up into — ordinary composite tag groups
   * (isCompositeGroup: true) AND a leaf-turned-settlement-parent (isCompositeGroup: false but
   * carrying a compositeGroupNodeId). A leaf-group's children (its settlement row) are scoped to
   * that leaf-group ALONE: its own rolled-up value already carries their contribution, so any
   * ancestor beyond it must not ALSO count them independently, or the settlement double-counts
   * (once via the leaf-group's own updated value, once as an independently-reachable leaf further
   * up) — same bug fixed server-side.
   *
   * All the lookup maps here (including the settlement/cash-account/contributing-leaves ones used
   * by recomputeSettlementForEdit) are pure row-identity topology — which row maps to what — which
   * never changes while editing (only cell values do), so this whole index is built once per load
   * and reused across every edit via the WeakMap cache below, rather than re-scanning all rows on
   * every keystroke.
   */
  function buildGroupIndex(rows: ProFormaLineItem[]): ProFormaGroupIndex {
    const groupChildren = buildGroupChildren(rows);

    const groupsByNodeId = new Map<number, ProFormaLineItem>();
    const leafGroupNodeIds = new Set<number>();
    let rootGroup: ProFormaLineItem | null = null;
    for (const row of rows) {
      const data = row.data;
      if (typeof data?.compositeGroupNodeId === "number") {
        groupsByNodeId.set(data.compositeGroupNodeId, row);
        if (data.isCompositeGroup === true) {
          if (typeof data.compositeParentGroupNodeId !== "number") {
            rootGroup = row;
          }
        } else {
          leafGroupNodeIds.add(data.compositeGroupNodeId);
        }
      }
    }

    const leaves = rows.filter(r => r.data && r.data.isCompositeGroup === false);

    const descendantLeavesByGroup = new Map<number, ProFormaLineItem[]>();
    groupsByNodeId.forEach((group, groupId) => {
      const descendantGroupIds = getDescendantGroupIds(groupId, groupChildren);
      descendantGroupIds.add(groupId);

      // Keep this group's own id always; drop every OTHER leaf-group id so its children stay
      // invisible to ancestors beyond it (see leafGroupNodeIds comment above).
      const validParentIds = new Set(Array.from(descendantGroupIds).filter(
        id => id === groupId || !leafGroupNodeIds.has(id)));

      descendantLeavesByGroup.set(groupId, leaves.filter(leaf =>
        leaf !== group
        && typeof leaf.data.compositeParentGroupNodeId === "number"
        && validParentIds.has(leaf.data.compositeParentGroupNodeId)));
    });

    const leafByInvestmentId = new Map<number, ProFormaLineItem>();
    const contributingLeavesByCashAccountInvestmentId = new Map<number, ProFormaLineItem[]>();
    for (const leaf of leaves) {
      if (typeof leaf.data.investmentId === "number") {
        leafByInvestmentId.set(leaf.data.investmentId, leaf);
      }
      if (typeof leaf.data.cashAccountInvestmentId === "number") {
        const id = leaf.data.cashAccountInvestmentId;
        if (!contributingLeavesByCashAccountInvestmentId.has(id)) {
          contributingLeavesByCashAccountInvestmentId.set(id, []);
        }
        contributingLeavesByCashAccountInvestmentId.get(id)!.push(leaf);
      }
    }

    const settlementLeafByGroupNodeId = new Map<number, ProFormaLineItem>();
    leafGroupNodeIds.forEach(groupId => {
      const settlementLeaf = descendantLeavesByGroup.get(groupId)?.[0];
      if (settlementLeaf) {
        settlementLeafByGroupNodeId.set(groupId, settlementLeaf);
      }
    });

    return {
      groupsByNodeId,
      descendantLeavesByGroup,
      rootGroup,
      leafGroupNodeIds,
      leafByInvestmentId,
      settlementLeafByGroupNodeId,
      contributingLeavesByCashAccountInvestmentId,
    };
  }

  /**
   * Per-table cache of the grouping topology, keyed by the table instance (so multiple ProForma
   * tables on a page each keep their own) and tagged with the `dataView.rows` array it was built
   * from. A report reload rebinds `lineItems` to a new array, which changes that identity and
   * transparently invalidates the cache; in-place cell edits keep the same array and hit the cache.
   * A WeakMap keeps the table free of bespoke fields and lets entries GC with the table.
   */
  type GroupIndexCacheEntry = { rowsRef: ProFormaLineItem[]; index: ProFormaGroupIndex };
  const groupIndexCache = new WeakMap<ReportTable, GroupIndexCacheEntry>();

  function getGroupIndex(table: ReportTable, rows: ProFormaLineItem[]): ProFormaGroupIndex {
    const cached = groupIndexCache.get(table);
    if (cached && cached.rowsRef === rows) {
      return cached.index;
    }
    const index = buildGroupIndex(rows);
    groupIndexCache.set(table, { rowsRef: rows, index });
    return index;
  }

  /**
   * Sort interval codes by their trailing period number ("ForwardPeriods-1" before
   * "ForwardPeriods-2") so a period-to-period cascade (recomputeGroupTotalsCascading) processes them
   * in chronological order. Mirrors the "Name-Number" convention recomputeRowForChange's own cascade
   * already parses (`parsed.intervalCode.split("-")`).
   */
  function sortIntervalCodesByPeriod(codes: string[]): string[] {
    return [...codes].sort((a, b) => {
      const numA = parseInt(a.split("-").pop() ?? "", 10);
      const numB = parseInt(b.split("-").pop() ?? "", 10);
      return (isNaN(numA) || isNaN(numB)) ? 0 : numA - numB;
    });
  }

  /**
   * Recompute an ORDINARY composite tag group's per-interval totals as the SUM of its descendant
   * leaves' values (own value always 0, fully recomputed from the sum — unchanged from before
   * #44970). Mirrors the per-group summation in ProFormaEditorReportBuilder.CalculateGroupLineItemValues;
   * the descendant-leaf list is supplied pre-computed from the cached group index. Some groups (e.g.
   * "All", an ordinary asset-class group) arrive from the loader already carrying a real
   * pre-populated aggregate, so this always overwrites rather than preserving it — see
   * recomputeGroupTotalsCascading for the leaf-group case.
   */
  function recomputeGroupTotals(group: ProFormaLineItem, descendantLeaves: ProFormaLineItem[]): void {
    for (const code of getIntervalCodes(group.data)) {
      for (const base of GROUP_ROLLUP_BASES) {
        const sum = descendantLeaves.reduce((acc, leaf) => acc + (getIntervalField(leaf.data, base, code) ?? 0), 0);
        setIntervalField(group.data, base, code, sum);
      }
    }
  }

  /**
   * Recompute a leaf-turned-settlement-parent's (a cash-account leaf's) own proFormaTx and endValue,
   * cascading period-to-period exactly like a directly-edited leaf's own cascade
   * (recomputeRowForChange): each period's own contribution carries the PREVIOUS period's
   * fully-settled endValue forward as its effective start, rather than whatever static value the
   * server loaded for that period. The cash account leaf is never itself directly edited (only ever
   * updated indirectly, via the settlement), so it never goes through recomputeRowForChange's own
   * cascade — without this fix, a settlement recomputed in period 1 never propagated into period 2+
   * (or into a period the server never populated a startValue for at all): a real bug, reported as
   * "cash auto-balance isn't rolling up to its group rows" in a 2+ forward-period report.
   *
   * proFormaTx now behaves like any other leaf's (editable, End = Start + ProFormaTx) except that its
   * value is auto-updated by edits on the OTHER leaves that resolve to this cash account: displayed
   * proFormaTx = this leaf's own GENUINE activity + the live settlement sum.
   *
   * "Genuine own" is NOT simply `baseline`'s proFormaTx: the server itself now folds the settlement
   * into the loaded value the exact same way (see CalculateGroupLineItemValues), so `baseline` is
   * already (genuine own AT LOAD) + (settlement sum AT LOAD), not the genuine own amount alone. Using
   * it directly would double-count whatever settlement the server had already folded in the moment
   * ANY contributing trade is edited (reported live: editing a trade kept the settlement's OLD amount
   * baked into the cash leaf's math alongside the new one). The fix: subtract the descendant leaves'
   * OWN baseline (their settlement contribution as loaded) from this leaf's baseline to recover the
   * genuine own figure --
   *     genuineOwn = baseline(this leaf's proFormaTx) - baseline(descendant leaves' endValue, summed)
   * -- then add the LIVE (current, not baseline) settlement sum on top. Both terms of genuineOwn come
   * from `baseline`, which is immutable for the session, so this is idempotent across repeated edits
   * (no compounding), while still tracking a live settlement change correctly.
   *
   * proFormaTxPercentage/netAmountIn are the leaf's own REAL custody-derived values (or a direct edit)
   * and are still never touched here — only startValue (the carry-forward), proFormaTx, and endValue
   * move.
   */
  function recomputeGroupTotalsCascading(
    group: ProFormaLineItem,
    descendantLeaves: ProFormaLineItem[],
    baseline: ProFormaBaseline | null,
  ): void {
    let lastEndValue: number | null = null;
    for (const code of sortIntervalCodesByPeriod(getIntervalCodes(group.data))) {
      if (lastEndValue !== null) {
        setIntervalField(group.data, ProFormaIntervalField.startValue, code, lastEndValue);
      }
      const ownStartValue = getIntervalField(group.data, ProFormaIntervalField.startValue, code) ?? 0;
      const ownNetAmountIn = getIntervalField(group.data, ProFormaIntervalField.netAmountIn, code) ?? 0;
      const baselineOwnProFormaTx = getBaselineIntervalField(baseline, group, ProFormaIntervalField.proFormaTx, code) ?? 0;
      const baselineSettlementSum = descendantLeaves.reduce(
        (acc, leaf) => acc + (getBaselineIntervalField(baseline, leaf, ProFormaIntervalField.endValue, code) ?? 0), 0);
      const genuineOwnProFormaTx = baselineOwnProFormaTx - baselineSettlementSum;
      const settlementSum = descendantLeaves.reduce(
        (acc, leaf) => acc + (getIntervalField(leaf.data, ProFormaIntervalField.endValue, code) ?? 0), 0);
      const newProFormaTx = genuineOwnProFormaTx + settlementSum;
      setIntervalField(group.data, ProFormaIntervalField.proFormaTx, code, newProFormaTx);
      lastEndValue = ownStartValue + ownNetAmountIn + newProFormaTx;
      setIntervalField(group.data, ProFormaIntervalField.endValue, code, lastEndValue);
    }
  }

  /**
   * After a leaf row has been recomputed, walk its ancestor chain (`compositeParentGroupNodeId`) and
   * re-aggregate every composite group it belongs to so the group totals match what a fresh server
   * build would produce. This is the client-side ("do it live") counterpart of
   * ProFormaEditorReportBuilder.CalculateGroupLineItemValues; the table rebuild that follows the cell
   * commit re-renders the group rows from this mutated data.
   *
   * If `leaf` is itself a leaf-turned-settlement-parent (a cash-account row), its own totals are
   * cascaded first (recomputeGroupTotalsCascading) before walking further up — covers both `leaf`
   * being the cash account itself (reached only via recomputeSettlementForEdit; its own cell is
   * non-editable, see ProFormaEditor.js's isEditable override) and `leaf` being an ordinary trade whose
   * ancestor chain happens to pass through a DIFFERENT leaf-group. All "cross-row" aggregation for a
   * leaf-group — its own cascade AND its ancestors' sums — lives here.
   */
  export function rollUpToAncestorGroups(table: ReportTable, leaf: ProFormaLineItem): void {
    const data = leaf?.data;
    if (!data || data.isCompositeGroup === true) {
      return;
    }

    const rows = getAllLineItems(table);
    if (!rows.length) {
      return;
    }

    const { groupsByNodeId, descendantLeavesByGroup, leafGroupNodeIds } = getGroupIndex(table, rows);
    const baseline = ensureBaseline(table);

    if (typeof data.compositeGroupNodeId === "number") {
      recomputeGroupTotalsCascading(leaf, descendantLeavesByGroup.get(data.compositeGroupNodeId) ?? [], baseline);
    }

    let ancestorId = data.compositeParentGroupNodeId;
    const visited = new Set<number>();
    while (typeof ancestorId === "number" && !visited.has(ancestorId)) {
      visited.add(ancestorId);
      const group = groupsByNodeId.get(ancestorId);
      if (!group) {
        break;
      }
      const descendantLeaves = descendantLeavesByGroup.get(ancestorId) ?? [];
      if (leafGroupNodeIds.has(ancestorId)) {
        recomputeGroupTotalsCascading(group, descendantLeaves, baseline);
      } else {
        recomputeGroupTotals(group, descendantLeaves);
      }
      ancestorId = group.data.compositeParentGroupNodeId;
    }
  }

  /**
   * If `editedLeaf` resolves a Default Tx Cash Account (`cashAccountInvestmentId`), live-recompute
   * that account's Investment Activity Settlement row as the aggregated inverse of every trade leaf
   * currently resolving to the same account (mirrors
   * ProFormaEditorReportBuilder.AddInvestmentActivitySettlementRows), then roll that up the cash
   * account leaf's own chain (rollUpToAncestorGroups cascades the leaf's own totals first, since it's
   * itself a leaf-group, before walking its ordinary ancestors). A no-op when the edited leaf has no
   * resolved cash account, or the cash-account/settlement rows aren't present (they always should be,
   * per the "always present" design — see AddInvestmentActivitySettlementRows).
   */
  export function recomputeSettlementForEdit(table: ReportTable, editedLeaf: ProFormaLineItem): void {
    const cashAccountInvestmentId = editedLeaf?.data?.cashAccountInvestmentId;
    if (typeof cashAccountInvestmentId !== "number") {
      return;
    }

    const rows = getAllLineItems(table);
    if (!rows.length) {
      return;
    }

    const { leafByInvestmentId, settlementLeafByGroupNodeId, contributingLeavesByCashAccountInvestmentId } =
      getGroupIndex(table, rows);

    const cashAccountLeaf = leafByInvestmentId.get(cashAccountInvestmentId);
    const cashAccountGroupNodeId = cashAccountLeaf?.data?.compositeGroupNodeId;
    if (!cashAccountLeaf || typeof cashAccountGroupNodeId !== "number") {
      return;
    }

    const settlementLeaf = settlementLeafByGroupNodeId.get(cashAccountGroupNodeId);
    if (!settlementLeaf) {
      return;
    }

    // Every trade leaf (including the one just edited) resolving to this same cash account.
    const contributingLeaves = contributingLeavesByCashAccountInvestmentId.get(cashAccountInvestmentId) ?? [];

    // Codes come from the cash account leaf, not the settlement row: the settlement row
    // intentionally has no netAmountIn (see AddInvestmentActivitySettlementRows -- only
    // proFormaTx/endValue are set, so it renders "--" elsewhere), and getIntervalCodes keys off
    // netAmountIn_<code>. Reading codes off settlementLeaf.data would find none, silently
    // no-op-ing this entire loop on every call.
    for (const code of getIntervalCodes(cashAccountLeaf.data)) {
      const sum = contributingLeaves.reduce(
        (acc, leaf) => acc + (getIntervalField(leaf.data, ProFormaIntervalField.proFormaTx, code) ?? 0), 0);
      setIntervalField(settlementLeaf.data, ProFormaIntervalField.proFormaTx, code, -sum);
      setIntervalField(settlementLeaf.data, ProFormaIntervalField.endValue, code, -sum);
    }

    rollUpToAncestorGroups(table, cashAccountLeaf);
  }

  /**
   * Recompute the Allocation (`allocation`) for every row — leaf and group — as its share of the
   * whole portfolio's value for that period: row endValue / root ("All") endValue. This is distinct
   * from Target Allocation (`benchmarkAllocation`, the benchmark policy weight), which is left
   * untouched. An edit changes end values — and therefore the root total — so every row's allocation
   * shifts, not just the edited leaf's ancestors; we recompute them all across all periods. When the
   * root total is missing or zero the allocation is cleared rather than dividing by zero. Mirrors the
   * initial-load computation in ProFormaEditorReportBuilder.CalculateGroupLineItemValues so load and
   * edit agree.
   */
  export function recomputeAllocations(table: ReportTable): void {
    const rows = getAllLineItems(table);
    if (!rows.length) {
      return;
    }

    const { rootGroup } = getGroupIndex(table, rows);
    if (!rootGroup) {
      return;
    }

    for (const code of getIntervalCodes(rootGroup.data)) {
      const rootEndValue = getIntervalField(rootGroup.data, "endValue", code);
      for (const row of rows) {
        if (!row.data) {
          continue;
        }
        const endValue = getIntervalField(row.data, "endValue", code);
        const allocation = (rootEndValue !== null && rootEndValue !== 0 && endValue !== null)
          ? endValue / rootEndValue
          : null;
        setIntervalField(row.data, "allocation", code, allocation);
      }
    }
  }

  /**
   * Entry point for `DataTablePartSettings.onRowChanged` wired up by ProFormaEditor.js.
   * Invoked by DataTableControl with the firing table as `this`, so a leaf edit recomputes its own
   * row, live-updates the Investment Activity Settlement line it affects (either as the edited
   * row's own settlement-parent, or as a trade resolving to a DIFFERENT cash account elsewhere —
   * #44970), rolls up into that table's ancestor group totals, then re-derives each group's
   * allocation.
   */
  export function onRowChanged(table: ReportTable, row: ProFormaLineItem, change: CellChange): void {
    // `this` is bound to the firing table by DataTableControl.onRowCellChanged (`.call(this, ...)`).
    // Guard against a detached call so we degrade to a no-op instead of throwing in the helpers.
    if (!table || !table.columnSet) {
      console.error("ProForma.onRowChanged: expected to be called with the DataTableControl as `this`");
      return;
    }
    recomputeRowForChange(table, row, change);

    // If the edited row is a trade resolving to a cash account elsewhere, recompute THAT account's
    // settlement and roll it up too.
    recomputeSettlementForEdit(table, row);

    // rollUpToAncestorGroups handles both cases: if `row` is itself a cash-account leaf (a direct
    // edit to its own cell -- a manual deposit/withdrawal unrelated to any trade), it cascades the
    // leaf's own totals before walking up; otherwise it just walks row's ordinary ancestor chain.
    rollUpToAncestorGroups(table, row);

    recomputeAllocations(table);
  }

  // ---------------------------------------------------------------------------
  // Baseline snapshot — support for Save (diff) and Revert (restore)
  // ---------------------------------------------------------------------------

  /**
   * A snapshot of the editor's line-item values as they were loaded from the server, taken before
   * the user makes any edits. Save diffs the live rows against it (delta -> buy/sell tx); Revert
   * copies it back over the live rows. `dataByRow` holds a shallow clone of each row's `data` —
   * every value in that dict is a primitive, so a shallow copy is a complete, independent copy.
   */
  export type ProFormaBaseline = {
    rowsRef: ProFormaLineItem[];
    dataByRow: Map<ProFormaLineItem, ProFormaLineItemData>;
  };

  /** Shallow copy of a row's data dict — sufficient because every value in it is a primitive. */
  function cloneRowData(data: ProFormaLineItemData): ProFormaLineItemData {
    return Object.assign({}, data);
  }

  /** Build a fresh baseline snapshot from the given rows (clones each row's data). */
  export function captureBaseline(rows: ProFormaLineItem[]): ProFormaBaseline {
    const dataByRow = new Map<ProFormaLineItem, ProFormaLineItemData>();
    for (const row of rows) {
      if (row?.data) {
        dataByRow.set(row, cloneRowData(row.data));
      }
    }
    return { rowsRef: rows, dataByRow };
  }

  /**
   * Per-table baseline cache, tagged with the `dataView.rows` array it was captured from — the same
   * invalidation strategy as the group-index cache. A report run rebinds `lineItems` to a new array
   * (identity changes -> recapture, so the baseline tracks the freshly loaded values); in-place cell
   * edits keep the same array (identity stable -> the load-time snapshot is preserved across edits).
   */
  const baselineCache = new WeakMap<ReportTable, ProFormaBaseline>();

  /**
   * Return the load-time baseline for the table's current rows, capturing it on first use after a
   * (re)load. Safe to call on every `dataViewUpdated`: it only re-captures when the rows array
   * identity changes, so post-edit rebuilds reuse the original snapshot. Returns null before any
   * data has loaded.
   */
  export function ensureBaseline(table: ReportTable): ProFormaBaseline | null {
    const rows = getAllLineItems(table);
    if (!rows.length) {
      return null;
    }
    const cached = baselineCache.get(table);
    if (cached && cached.rowsRef === rows) {
      return cached;
    }
    const baseline = captureBaseline(rows);
    baselineCache.set(table, baseline);
    return baseline;
  }

  /** The originally-loaded value of an interval field for a row, or null if not in the baseline. */
  export function getBaselineIntervalField(
    baseline: ProFormaBaseline | null,
    row: ProFormaLineItem,
    base: ProFormaIntervalFieldBase,
    code: string,
  ): number | null {
    const baselineData = baseline?.dataByRow.get(row);
    if (!baselineData) {
      return null;
    }
    return getIntervalField(baselineData, base, code);
  }

  /**
   * Discard all edits: copy the baseline values back over the live row objects. Each row gets a
   * fresh clone so repeated reverts stay correct and subsequent edits never mutate the snapshot.
   * Returns true if a baseline was available to restore. The caller is responsible for triggering a
   * table rebuild so the restored values re-render.
   */
  export function restoreBaseline(table: ReportTable): boolean {
    const baseline = baselineCache.get(table);
    if (!baseline) {
      return false;
    }
    for (const row of getAllLineItems(table)) {
      const baselineData = baseline.dataByRow.get(row);
      if (baselineData) {
        row.data = cloneRowData(baselineData);
      }
    }
    return true;
  }

  /**
   * Reset the baseline to the table's current values. Called after a successful save so the grid is
   * no longer "dirty" and any further edits diff from the just-saved state (rather than re-emitting
   * the transactions that were already persisted).
   */
  export function resetBaseline(table: ReportTable): void {
    const rows = getAllLineItems(table);
    if (rows.length) {
      baselineCache.set(table, captureBaseline(rows));
    }
  }

  // ---------------------------------------------------------------------------
  // Save — collect edited cells as pro forma transactions to persist
  // ---------------------------------------------------------------------------

  /** Below this absolute net-amount delta a cell is treated as unchanged (float-input noise). */
  const EDIT_DELTA_EPSILON = 1e-6;

  /**
   * Transaction-type tag ids for editor-created pro forma transactions — mirror the server constants
   * SystemTransactionTypeTagIds (Buy = 1, Sell = 2) and SystemTagSetIds.TransactionType (102).
   */
  export const TransactionTypeTagSetId = 102;
  export const BuyTagId = 1;
  export const SellTagId = 2;

  /**
   * Per-table cache of the cash-account gate, tagged with the `dataView.rows` array it was computed
   * from — same invalidation strategy as the group-index/baseline caches. `ownerHasCashAccount` is a
   * server flag that doesn't change while editing, so this is scanned once per load and reused across
   * the many post-edit rebuilds that re-run the gate; a report reload rebinds `rows` and recomputes.
   */
  type CashAccountGateCacheEntry = { rowsRef: ProFormaLineItem[]; hasOwnerWithoutCashAccount: boolean };
  const cashAccountGateCache = new WeakMap<ReportTable, CashAccountGateCacheEntry>();

  /**
   * True if any savable (investment-bearing) leaf belongs to an owner without a default cash account,
   * per the server-emitted `ownerHasCashAccount` flag — used to disable Save, since a pro forma
   * buy/sell needs a cash account to offset against. Leaves where the flag is absent are treated as
   * allowed, so the gate only blocks on an explicit `false` from the builder. Cached per load: the
   * flag is fixed for a given report while the gate is re-checked on every edit.
   */
  export function checkHasOwnerWithoutCashAccount(table: ReportTable): boolean {
    const rows = getAllLineItems(table);
    const cached = cashAccountGateCache.get(table);
    if (cached && cached.rowsRef === rows) {
      return cached.hasOwnerWithoutCashAccount;
    }

    let hasOwnerWithoutCashAccount = false;
    for (const row of rows) {
      const data = row.data;
      if (data
        && data.isCompositeGroup === false
        && typeof data.investmentId === "number"
        && data.ownerHasCashAccount === false) {
        hasOwnerWithoutCashAccount = true;
        break;
      }
    }

    cashAccountGateCache.set(table, { rowsRef: rows, hasOwnerWithoutCashAccount });
    return hasOwnerWithoutCashAccount;
  }

  /**
   * One pro forma transaction to persist for a single edited period cell — the wire shape the
   * SaveProFormaEditorTransactions endpoint binds to (a subset of ProFormaTransactionEntryEditModel):
   * a standalone, signed-net transaction dated to the period end. A real-investment leaf sets
   * `investmentId`; a group scaffold row instead sets `propertyAssociationId`/`tagAssociationId`
   * — the two identity shapes are mutually exclusive.
   */
  export type ProFormaEditorEditedTx = {
    investmentId?: number;
    propertyAssociationId?: number;
    tagAssociationId?: number;
    effectiveDayId: number;
    netAmountIn: number;
    netPercentAmountIn: number;
    transactionTypeTagId: number;
    transactionTypeTagSetId: number;
  };

  export type CollectEditedTransactionsOptions = {
    /** Tag id applied when the period delta is positive (an inflow / buy). */
    buyTagId: number;
    /** Tag id applied when the period delta is negative (an outflow / sell). */
    sellTagId: number;
    /** Tag set the buy/sell tag ids belong to. */
    tagSetId: number;
  };

  /**
   * Period-end day id is a per-interval constant carried on line items as `intervalToDayId_<code>`
   * (emitted by the IntervalStats builder on real-investment leaves). Build a code -> end-day-id
   * lookup by scanning every row for the first that carries it, so a leaf missing the key can still
   * be dated from a sibling in the same period.
   */
  function buildPeriodEndByCode(rows: ProFormaLineItem[]): Record<string, number> {
    const periodEndByCode: Record<string, number> = {};
    for (const row of rows) {
      if (!row.data) {
        continue;
      }
      for (const code of getIntervalCodes(row.data)) {
        if (!(code in periodEndByCode)) {
          const endDayId = getIntervalField(row.data, "intervalToDayId", code);
          if (endDayId !== null) {
            periodEndByCode[code] = endDayId;
          }
        }
      }
    }
    return periodEndByCode;
  }

  /**
   * Diff the live editor rows against the load-time baseline and produce one transaction per changed
   * period cell. Savable rows are either an investment-bearing leaf or a group scaffold row — group
   * rows themselves aren't edited, and owner/holding-only rows carry no investment reference, so
   * edits elsewhere, or on a period whose end date can't be resolved, are skipped. Each emitted tx
   * is a standalone signed-net delta (`current - loaded`) dated to the period end and
   * typed buy/sell by the delta's sign.
   */
  export function collectEditedTransactions(
    table: ReportTable,
    options: CollectEditedTransactionsOptions,
  ): ProFormaEditorEditedTx[] {
    const baseline = ensureBaseline(table);
    const rows = getAllLineItems(table);
    if (!baseline || !rows.length) {
      return [];
    }

    const periodEndByCode = buildPeriodEndByCode(rows);
    const edits: ProFormaEditorEditedTx[] = [];

    for (const row of rows) {
      const data = row.data;
      if (!data || data.isCompositeGroup !== false) {
        continue;
      }

      // A leaf-group's own proFormaTx (see recomputeGroupTotalsCascading) is auto-derived from OTHER
      // leaves, not a user edit — same exclusion as isEditable in ProFormaEditor.js. If it weren't
      // excluded here, its baseline-vs-current delta (== the live settlement sum) would be submitted
      // as a phantom standalone edit on the cash account's own investmentId, double-booking against
      // the offsetting cash leg SaveProFormaEditorTransactionsAsync already creates per saved trade.
      if (typeof data.compositeGroupNodeId === "number") {
        continue;
      }

      const isScaffold = data.isProFormaScaffold === true;
      const hasInvestmentIdentity = typeof data.investmentId === "number";
      const hasScaffoldIdentity =
        isScaffold &&
        typeof data.propertyAssociationId === "number" &&
        typeof data.tagAssociationId === "number";

      if (!hasInvestmentIdentity && !hasScaffoldIdentity) {
        continue;
      }

      for (const code of getIntervalCodes(data)) {
        const current = getIntervalField(data, "proFormaTx", code) ?? 0;
        const loaded = getBaselineIntervalField(baseline, row, "proFormaTx", code) ?? 0;
        const delta = current - loaded;
        if (Math.abs(delta) <= EDIT_DELTA_EPSILON) {
          continue;
        }

        const effectiveDayId = periodEndByCode[code];
        if (typeof effectiveDayId !== "number") {
          // No resolvable period-end for this interval — can't date the tx, so skip it.
          continue;
        }

        // Persist the percentage delta too. The builder rebuilds proFormaTxPercentage on reload by
        // summing each saved tx's NetPercentAmountIn, so omitting it makes the % column reload as 0.
        // We diff whatever the grid currently shows for % — editing the % cell drives the amount,
        // while editing the amount clears the % (see recomputePeriodForRowChange) — so this captures
        // the user's actual % change rather than a value re-derived here.
        const currentPercent = getIntervalField(data, "proFormaTxPercentage", code) ?? 0;
        const loadedPercent = getBaselineIntervalField(baseline, row, "proFormaTxPercentage", code) ?? 0;

        edits.push({
          ...(hasInvestmentIdentity
            ? { investmentId: data.investmentId }
            : { propertyAssociationId: data.propertyAssociationId, tagAssociationId: data.tagAssociationId }),
          effectiveDayId,
          netAmountIn: delta,
          netPercentAmountIn: currentPercent - loadedPercent,
          transactionTypeTagId: delta > 0 ? options.buyTagId : options.sellTagId,
          transactionTypeTagSetId: options.tagSetId,
        });
      }
    }

    return edits;
  }
}
